import { Model } from "..";
import { getDefaultName } from "../../helper/setDefaultName";
import canvas from '@/services/LayoutCanvas';
import { DIODE, JUMPER, RLC_TYPES } from "../../PCBHelper";

// victimAggressors: { aggressorNets: [ { signal, net} ] }
function getAggressorSignalNets(victimAggressors = []) {
  const aggressorNets = victimAggressors.map(item => item.aggressorNets).flat();
  let signalNets = {};
  aggressorNets.forEach(({ net, signal }) => {
    if (!signalNets[signal]) {
      signalNets[signal] = [net];
    }

    if (!signalNets[signal].includes(net)) {
      signalNets[signal].push(net);
    }
  });

  return signalNets;
}

function getAllAggressorSignalNets(victims) {
  const nets = victims.map(victim => {
    const { victimAggressors } = victim;
    const aggressorNets = victimAggressors.map(item => item.aggressorNets).flat();
    return aggressorNets.map(({ net }) => net);
  }).flat();
  return [...new Set(nets)];
}

function generateVictimContentParams(searchData, interfaceName, interfaceType, signalNames) {
  const _signalNames = [...new Set(signalNames)];
  return [{
    interfaceName,
    interfaces: searchData.map((pcbData) => {
      const signalNetsList = (pcbData.victims || []).map(item => item.signalNets);

      return {
        content: signalNetsList.map((signalNets) => {
          const signalName = getDefaultName({ nameList: _signalNames, defaultKey: 'Victim', key: null, firstIndex: 1 });
          _signalNames.push(signalName);
          return {
            name: signalName,
            nets: signalNets,
            signalType: ""
          }
        }),
        designId: pcbData.pcbId,
        interfaceName: interfaceName,
        type: interfaceType,
      }
    })
  }];
}

function getVictimICComp(victimContent) {
  const components = victimContent.map(it => (it.content || {}).components || []).flat();
  const ICComp = components.find(it => it.type === "IC") || {};
  return {
    ...ICComp,
    pinModelsInfo: ICComp.pins && ICComp.pins.length > 0 ? ICComp.pins[0] : { usage: "Receiver", model: {} }
  };
}

function updateVictimComponentsModel(params, components) {
  let { model = {}, libType, deviceVcc = "", pinModelsInfo = {} } = params;
  const { pu, pinModels: newPinModels } = pinModelsInfo;
  const { enableVoltage, fileName, modelName, modelType, libraryId, version } = model;
  let compModel = {};
  // update IBIS file and component
  if (libType && libraryId && fileName) {
    compModel = {
      files: [{
        type: libType,
        libraryId,
        component: "",
        fileName,
        version
      }]
    }
  }

  const findPUIndex = (newPinModels || []).findIndex(item => item.pinName === "nd_pu");
  let newPU;
  if (findPUIndex > -1) {
    newPU = newPinModels[findPUIndex].voltage;
  }
  deviceVcc = deviceVcc || (newPU ? newPU : pu)
  const newModel = new Model({
    libType,
    libraryId: libraryId,
    fileName,
    modelType,
    modelName,
    enableVoltage,
    version
  });
  return components.map(item => {
    if (item.type !== "IC") {
      return item;
    }
    const pins = item.pins.map(pinItem => {
      return {
        ...pinItem,
        usage: "Receiver",
        model: newModel
      }
    });
    return {
      ...item,
      model: compModel,
      deviceVcc,
      pins
    }
  })
}

function getSelectCanvasData({ pcbId, signalNets, content }) {
  const { components = [] } = content;
  let pinsObj = {}, comps = [];

  for (let comp of components) {
    const { type, pins, name } = comp
    if ([...RLC_TYPES, JUMPER, DIODE].includes(type)) {
      const filterNets = pins.filter(item => signalNets.includes(item.net))
      if (filterNets.length) {
        // add connect select signal net comp
        comps.push(name);
      }

      const filterPin = pins.filter(item => signalNets.includes(item.net));
      if (filterPin && filterPin.length && pins.length > 2) {
        pinsObj[name] = filterPin.map(item => item.pin)
      }
    } else {
      const filterPin = pins.filter(item => signalNets.includes(item.net));
      if (filterPin.length) {
        pinsObj[name] = filterPin.map(item => item.pin)
        comps.push(name)
      }
    }
  }
  comps = [...new Set(comps)];
  const layout = canvas.getLayout(pcbId);
  let layers = layout && layout.findCurrentLayer ? layout.findCurrentLayer(comps) : [];
  layers = [...new Set(layers)];
  // get layers
  return { pinsObj, comps, layers }
}

export {
  getAggressorSignalNets,
  getAllAggressorSignalNets,
  getVictimICComp,
  generateVictimContentParams,
  updateVictimComponentsModel,
  getSelectCanvasData
};